import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { bearerTokenCache } from '@iplan/auth';
import { authClient } from './auth';
import { getEnv } from './env';

const axiosInstance = axios.create({
  baseURL: getEnv('VITE_API_BASE_URL') ?? '/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

axiosInstance.interceptors.request.use((config) => {
  const token = bearerTokenCache.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Module-level guard so a burst of concurrent 401s triggers at most one
// sign-in redirect (signinRedirect navigates away, so the rest are moot).
let redirectingToLogin = false;

type RetriableConfig = InternalAxiosRequestConfig & { _authRetried?: boolean };

/**
 * 401 handling: a stale/expired access token must re-authenticate, not silently
 * surface 401s forever (the app previously had no response interceptor, so a
 * stale token never redirected to login).
 *
 * 1. Pull the latest token from oidc-client-ts. If `automaticSilentRenew`
 *    already minted a fresh one that this request didn't carry, refresh the
 *    cache and retry the request ONCE.
 * 2. Otherwise (token genuinely expired + renew failed, or signed out) redirect
 *    to the OIDC login.
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;

    if (error.response?.status === 401 && original) {
      // Compare against the token THIS request actually carried, read from its
      // own Authorization header — NOT the shared bearerTokenCache. A concurrent
      // renewal could refresh the cache after this request went out, making
      // `fresh !== sentToken` wrongly false and skipping a valid retry.
      const authHeader = original.headers?.Authorization;
      const sentToken =
        typeof authHeader === 'string' ? authHeader.replace(/^Bearer\s+/i, '') : null;

      if (!original._authRetried) {
        original._authRetried = true;
        const fresh = await authClient.getAccessToken().catch(() => null);
        // Only retry if oidc-client-ts now has a *different* (renewed) token —
        // retrying with the same expired token would just 401 again.
        if (fresh && fresh !== sentToken) {
          bearerTokenCache.setToken(fresh);
          original.headers.Authorization = `Bearer ${fresh}`;
          return axiosInstance(original);
        }
      }

      // No working token → re-authenticate via the OIDC login redirect.
      if (!redirectingToLogin) {
        redirectingToLogin = true;
        bearerTokenCache.clearToken();
        try {
          await authClient.signIn();
        } catch {
          // If the redirect itself fails, allow a later 401 to try again.
          redirectingToLogin = false;
        }
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
